import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme,
  Paper,
  Divider,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import RestoreIcon from '@mui/icons-material/Restore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import TranslationService, {
  setNativeValue,
  getActiveFormLanguage,
  setActiveFormLanguage,
  isEligibleField,
} from '../utils/translationService';
import FormTranslateBar from './FormTranslateBar';

const TARGET_LANGUAGES = [
  { code: 'en', label: 'English', nativeName: 'English', flag: '🇺🇸', short: 'EN' },
  { code: 'te', label: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', short: 'TE' },
  { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', short: 'HI' },
  { code: 'ta', label: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', short: 'TA' },
];

function getEligibleFormFields(scope = document) {
  const allElements = Array.from(scope.querySelectorAll('input, textarea, .ql-editor, [contenteditable="true"]'));
  return allElements.filter(isEligibleField);
}

const UniversalFormTranslator = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  // Active dialogs portal targets
  const [dialogMounts, setDialogMounts] = useState([]);

  // Floating bar state (for full-page forms when no modal is open)
  const [hasFormFields, setHasFormFields] = useState(false);
  const [hasActiveDialog, setHasActiveDialog] = useState(false);
  const [filledFieldCount, setFilledFieldCount] = useState(0);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeLang, setActiveLang] = useState(() => getActiveFormLanguage());
  const [isMinimized, setIsMinimized] = useState(() => {
    try {
      return localStorage.getItem('gpn_form_translator_minimized') === 'true';
    } catch (e) {
      return false;
    }
  });

  // Multi-Language Studio Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [studioTab, setStudioTab] = useState('te');
  const [studioFields, setStudioFields] = useState([]);
  const [loadingStudio, setLoadingStudio] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const originalValuesRef = useRef(new Map());

  // ── TRANSLATE ALL FORM FIELDS ON SCREEN TO TARGET LANGUAGE ──
  const translateAllCurrentFields = useCallback(async (targetLang) => {
    const fields = getEligibleFormFields();
    if (fields.length === 0) return;

    for (const el of fields) {
      const isRich = el.isContentEditable || el.classList?.contains('ql-editor');
      const currentVal = isRich ? el.innerHTML : el.value;
      const plain = isRich ? (el.innerText || '').trim() : (el.value || '').trim();
      if (!plain) continue;

      // Remember English source text if current content has Latin letters
      if (/[a-zA-Z]/.test(plain)) {
        el.dataset.gpnOriginal = isRich ? currentVal : plain;
      }

      if (targetLang === 'en') {
        // Restore back to English: use saved original if available, otherwise translate to English
        const original = el.dataset.gpnOriginal;
        if (original) {
          if (isRich) {
            el.innerHTML = original;
            el.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            setNativeValue(el, original);
          }
        } else {
          // If no original was saved, dynamically translate foreign text back to English
          if (isRich) {
            const transHtml = await TranslationService.translateHtml(currentVal, 'en');
            el.innerHTML = transHtml;
            el.dispatchEvent(new Event('input', { bubbles: true }));
          } else {
            const trans = await TranslationService.translate(currentVal, 'en');
            setNativeValue(el, trans);
          }
        }
      } else {
        // Translate to target language (Telugu, Hindi, Tamil)
        // Prefer original English text to avoid loss of quality, fallback to currentVal
        const source = el.dataset.gpnOriginal || currentVal;
        if (isRich) {
          const transHtml = await TranslationService.translateHtml(source, targetLang);
          el.innerHTML = transHtml;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
          const trans = await TranslationService.translate(source, targetLang);
          setNativeValue(el, trans);
        }
      }
    }
  }, []);

  // Synchronize active language across navbar changes and form selector changes
  useEffect(() => {
    const handleAdminLang = (e) => {
      const lang = e.detail?.lang;
      if (lang && lang !== activeLang) {
        setActiveLang(lang);
        setActiveFormLanguage(lang);
        translateAllCurrentFields(lang);
      }
    };
    const handleFormLang = (e) => {
      const lang = e.detail;
      if (lang && lang !== activeLang) {
        setActiveLang(lang);
        translateAllCurrentFields(lang);
      }
    };

    window.addEventListener('gpn_admin_language_changed', handleAdminLang);
    window.addEventListener('gpn_form_lang_changed', handleFormLang);
    return () => {
      window.removeEventListener('gpn_admin_language_changed', handleAdminLang);
      window.removeEventListener('gpn_form_lang_changed', handleFormLang);
    };
  }, [activeLang, translateAllCurrentFields]);

  // ── GLOBAL AUTO-TRANSLATE: Converts English text into activeLang on blur and form submit ──
  // Removed mid-typing input debounce so user can type freely, fix mistakes, and backspace without interruption
  useEffect(() => {
    if (activeLang === 'en') return;

    let isTranslatingNow = false;

    const autoTranslateEl = async (el) => {
      if (!isEligibleField(el) || isTranslatingNow) return;

      const isRich = el.isContentEditable || el.classList?.contains('ql-editor');
      const plain = isRich ? (el.innerText || '').trim() : (el.value || '').trim();

      // Only translate on blur if there are Latin/English letters
      if (!plain || !/[a-zA-Z]/.test(plain)) return;

      // Remember English source text on the element
      el.dataset.gpnOriginal = isRich ? el.innerHTML : plain;

      try {
        isTranslatingNow = true;
        if (isRich) {
          const currentHtml = el.innerHTML;
          const translatedHtml = await TranslationService.translateHtml(currentHtml, activeLang);
          el.innerHTML = translatedHtml;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        } else {
          const currentVal = el.value;
          const translated = await TranslationService.translate(currentVal, activeLang);
          setNativeValue(el, translated);
        }
      } catch (err) {
        console.error('[UniversalAutoTranslator] Error:', err);
      } finally {
        isTranslatingNow = false;
      }
    };

    // 1. On blur: User has completed entering this field and moved away to the next field/control
    const handleBlur = (e) => {
      autoTranslateEl(e.target);
    };

    // 2. On Enter key in single-line text inputs: Trigger translation immediately
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT') {
        autoTranslateEl(e.target);
      }
    };

    // 3. Before form submission: Translate any remaining fields that haven't blurred yet
    const handleSubmit = async (e) => {
      const form = e.target;
      if (!form || !form.querySelectorAll) return;
      const inputs = Array.from(form.querySelectorAll('input, textarea, .ql-editor')).filter(isEligibleField);
      for (const el of inputs) {
        await autoTranslateEl(el);
      }
    };

    document.addEventListener('blur', handleBlur, true);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('submit', handleSubmit, true);

    return () => {
      document.removeEventListener('blur', handleBlur, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('submit', handleSubmit, true);
    };
  }, [activeLang]);

  // Detect and mount into any active Material UI DialogActions
  useEffect(() => {
    const scanDialogs = () => {
      const dialogActionsList = Array.from(document.querySelectorAll('.MuiDialogActions-root'));
      const newMounts = [];
      let foundOpenDialog = false;

      dialogActionsList.forEach((actionEl) => {
        const dialogPaper = actionEl.closest('.MuiDialog-paper');
        if (!dialogPaper) return;

        // Check if dialog contains form fields
        const fields = getEligibleFormFields(dialogPaper);
        if (fields.length === 0) return;

        foundOpenDialog = true;

        // Style DialogActions to accommodate left-aligned translator and right-aligned buttons
        actionEl.style.display = 'flex';
        actionEl.style.justifyContent = 'space-between';
        actionEl.style.alignItems = 'center';
        actionEl.style.flexWrap = 'wrap';

        // Check if mount container exists or create one
        let mountContainer = actionEl.querySelector('.gpn-dialog-translate-mount');
        if (!mountContainer) {
          mountContainer = document.createElement('div');
          mountContainer.className = 'gpn-dialog-translate-mount';
          mountContainer.setAttribute('data-gpn-translator', 'true');
          mountContainer.style.marginRight = 'auto';
          actionEl.insertBefore(mountContainer, actionEl.firstChild);
        }

        newMounts.push({ key: actionEl, container: mountContainer });
      });

      setHasActiveDialog(foundOpenDialog);

      setDialogMounts((prev) => {
        if (prev.length !== newMounts.length) return newMounts;
        const changed = newMounts.some((m, idx) => m.container !== prev[idx]?.container);
        return changed ? newMounts : prev;
      });

      // Also check page-level fields
      const allFields = getEligibleFormFields();
      setHasFormFields(allFields.length > 0);

      let filled = 0;
      allFields.forEach((el) => {
        const val = el.isContentEditable ? (el.innerText || '').trim() : (el.value || '').trim();
        if (val.length > 0) filled++;
      });
      setFilledFieldCount(filled);
    };

    scanDialogs();
    const interval = setInterval(scanDialogs, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleToggleMinimize = () => {
    setIsMinimized((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('gpn_form_translator_minimized', String(next));
      } catch (e) {}
      return next;
    });
  };

  const handleTranslateForm = async (targetLang) => {
    if (isTranslating) return;

    const fields = getEligibleFormFields();
    if (fields.length === 0) {
      setToast({
        open: true,
        message: 'No form fields found on current screen.',
        severity: 'info',
      });
      return;
    }

    setIsTranslating(true);
    setActiveLang(targetLang);
    setActiveFormLanguage(targetLang);

    try {
      await translateAllCurrentFields(targetLang);
      const targetObj = TARGET_LANGUAGES.find((l) => l.code === targetLang) || { nativeName: targetLang, label: targetLang };
      setToast({
        open: true,
        message:
          targetLang === 'en'
            ? 'Form restored back to English.'
            : `Form successfully translated to ${targetObj.label} (${targetObj.nativeName})! Ready to post.`,
        severity: 'success',
      });
    } catch (err) {
      console.error('[UniversalFormTranslator] Translate error:', err);
      setToast({
        open: true,
        message: 'Failed to translate some fields. Please check your network connection.',
        severity: 'error',
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const handleOpenStudio = async () => {
    const fields = getEligibleFormFields();
    const captured = [];

    fields.forEach((el, index) => {
      const isRich = el.isContentEditable;
      const currentVal = isRich ? (el.innerText || '').trim() : (el.value || '').trim();
      const orig = originalValuesRef.current.get(el);
      const enVal = orig ? (typeof orig === 'string' && orig.includes('<') ? orig.replace(/<[^>]*>/g, '').trim() : orig) : currentVal;

      if (!enVal) return;

      let label = el.getAttribute('aria-label') || el.getAttribute('placeholder') || el.getAttribute('name');
      if (!label) {
        const parentFormGroup = el.closest('.MuiFormControl-root, .form-group, label');
        const labelEl = parentFormGroup?.querySelector('label, .MuiInputLabel-root');
        label = labelEl?.innerText || `Field #${index + 1}`;
      }

      captured.push({
        id: index,
        element: el,
        label: label.replace(/\s*\*\s*$/, '').trim(),
        en: enVal,
        te: '',
        hi: '',
        ta: '',
      });
    });

    if (captured.length === 0) {
      setToast({
        open: true,
        message: 'No filled fields detected. Please enter some text into the form first.',
        severity: 'warning',
      });
      return;
    }

    setStudioFields(captured);
    setModalOpen(true);
    setLoadingStudio(true);

    try {
      const updated = await Promise.all(
        captured.map(async (f) => {
          const [te, hi, ta] = await Promise.all([
            TranslationService.translate(f.en, 'te'),
            TranslationService.translate(f.en, 'hi'),
            TranslationService.translate(f.en, 'ta'),
          ]);
          return { ...f, te, hi, ta };
        })
      );
      setStudioFields(updated);
    } catch (err) {
      console.error('[UniversalFormTranslator] Studio fetch error:', err);
    } finally {
      setLoadingStudio(false);
    }
  };

  const handleApplyStudioLanguage = (langCode) => {
    studioFields.forEach((f) => {
      const val = f[langCode] || f.en;
      if (!f.element) return;

      if (f.element.isContentEditable) {
        f.element.innerText = val;
        f.element.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        setNativeValue(f.element, val);
      }
    });

    setActiveLang(langCode);
    const targetObj = TARGET_LANGUAGES.find((l) => l.code === langCode);
    setToast({
      open: true,
      message: `Applied ${targetObj?.label || langCode} content to the form. You can now post!`,
      severity: 'success',
    });
    setModalOpen(false);
  };

  const handleCopyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <>
      {/* 1. Portals into all active Modal / Dialog actions bars */}
      {dialogMounts.map((m, idx) =>
        createPortal(<FormTranslateBar key={idx} />, m.container)
      )}

      {/* 2. Floating action bar for full page forms (visible when no modal dialog is open) */}
      {hasFormFields && !hasActiveDialog && (
        <Box
          data-gpn-translator="true"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1200,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Paper
            elevation={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.8,
              px: 1.5,
              borderRadius: '50px',
              background: isLight ? 'rgba(255, 255, 255, 0.95)' : 'rgba(15, 23, 42, 0.92)',
              backdropFilter: 'blur(20px)',
              border: isLight ? '1.5px solid #0D9488' : '1.5px solid rgba(76, 161, 175, 0.55)',
              boxShadow: isLight
                ? '0 10px 30px rgba(13, 148, 136, 0.22), 0 4px 12px rgba(0,0,0,0.08)'
                : '0 12px 35px rgba(0, 0, 0, 0.65), 0 0 15px rgba(76, 161, 175, 0.25)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 0.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: isLight ? 'rgba(13, 148, 136, 0.15)' : 'rgba(76, 161, 175, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isLight ? '#0D9488' : '#4CA1AF',
                }}
              >
                {isTranslating ? (
                  <CircularProgress size={16} sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }} />
                ) : (
                  <TranslateIcon sx={{ fontSize: 17 }} />
                )}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  fontSize: '0.8rem',
                  color: isLight ? '#0F172A' : '#F1F5F9',
                  display: { xs: 'none', sm: 'inline-block' },
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.3px',
                }}
              >
                Translate Form:
              </Typography>

              {filledFieldCount > 0 && (
                <Chip
                  size="small"
                  label={`${filledFieldCount} ${filledFieldCount === 1 ? 'field' : 'fields'}`}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    bgcolor: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.25)',
                    color: isLight ? '#0D9488' : '#64d2e2',
                    display: { xs: 'none', md: 'inline-flex' },
                  }}
                />
              )}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

            {!isMinimized && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                {TARGET_LANGUAGES.map((lang) => {
                  const isSelected = activeLang === lang.code;
                  return (
                    <Tooltip
                      key={lang.code}
                      title={
                        lang.code === 'en'
                          ? 'Restore form to English (Default)'
                          : `Translate filled form to ${lang.label} (${lang.nativeName}) to post`
                      }
                      arrow
                    >
                      <Button
                        size="small"
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() => handleTranslateForm(lang.code)}
                        disabled={isTranslating}
                        sx={{
                          textTransform: 'none',
                          minWidth: { xs: 44, sm: 54 },
                          px: { xs: 1, sm: 1.4 },
                          py: 0.5,
                          borderRadius: '20px',
                          fontSize: '0.78rem',
                          fontWeight: 700,
                          ...(isSelected
                            ? {
                                bgcolor: isLight ? '#0D9488' : '#0D9488',
                                color: '#FFFFFF',
                                borderColor: 'transparent',
                                boxShadow: '0 2px 8px rgba(13, 148, 136, 0.35)',
                                '&:hover': { bgcolor: '#0B7A70' },
                              }
                            : {
                                color: isLight ? '#334155' : 'rgba(255, 255, 255, 0.85)',
                                borderColor: isLight ? '#CBD5E1' : 'rgba(255, 255, 255, 0.2)',
                                bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.05)',
                                '&:hover': {
                                  bgcolor: isLight ? '#F1F5F9' : 'rgba(255, 255, 255, 0.1)',
                                  borderColor: isLight ? '#0D9488' : '#4CA1AF',
                                },
                              }),
                        }}
                      >
                        <span style={{ marginRight: 4 }}>{lang.flag}</span>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {lang.nativeName}
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          {lang.short}
                        </Box>
                      </Button>
                    </Tooltip>
                  );
                })}

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />

                <Tooltip title="Preview & Compare Telugu, Hindi, Tamil & English side-by-side" arrow>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleOpenStudio}
                    disabled={isTranslating}
                    startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '20px',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      px: 1.5,
                      py: 0.5,
                      color: isLight ? '#7C3AED' : '#C084FC',
                      borderColor: isLight ? 'rgba(124, 58, 237, 0.35)' : 'rgba(192, 132, 252, 0.35)',
                      bgcolor: isLight ? 'rgba(124, 58, 237, 0.06)' : 'rgba(124, 58, 237, 0.12)',
                      '&:hover': {
                        bgcolor: isLight ? 'rgba(124, 58, 237, 0.12)' : 'rgba(124, 58, 237, 0.22)',
                        borderColor: '#7C3AED',
                      },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                      Multi-Lang
                    </Box>{' '}
                    Studio
                  </Button>
                </Tooltip>
              </Box>
            )}

            <Tooltip title={isMinimized ? 'Expand Form Translator' : 'Minimize Translator'} arrow>
              <IconButton
                size="small"
                onClick={handleToggleMinimize}
                sx={{
                  color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.6)',
                  p: 0.5,
                  '&:hover': { color: isLight ? '#0F172A' : '#FFFFFF' },
                }}
              >
                {isMinimized ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Paper>
        </Box>
      )}

      {/* ── Multi-Language Form Studio Modal ── */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isLight ? '#FFFFFF' : '#182232',
            border: isLight ? '1.5px solid #CBD5E1' : '1.5px solid rgba(76, 161, 175, 0.45)',
            boxShadow: isLight ? '0 20px 50px rgba(15, 23, 42, 0.2)' : '0 25px 60px rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1.5,
            borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '8px',
                bgcolor: isLight ? 'rgba(13, 148, 136, 0.15)' : 'rgba(76, 161, 175, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: isLight ? '#0D9488' : '#4CA1AF',
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} color={isLight ? '#0F172A' : '#F8FAFC'}>
                Multi-Language Post Studio
              </Typography>
              <Typography variant="caption" color={isLight ? '#64748B' : 'rgba(255,255,255,0.6)'}>
                Review, edit, and post your form content in English, Telugu, Hindi, or Tamil
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5, pb: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={studioTab}
              onChange={(e, val) => setStudioTab(val)}
              variant="fullWidth"
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab value="te" label="🇮🇳 Telugu (తెలుగు)" sx={{ fontWeight: 700, textTransform: 'none' }} />
              <Tab value="hi" label="🇮🇳 Hindi (हिन्दी)" sx={{ fontWeight: 700, textTransform: 'none' }} />
              <Tab value="ta" label="🇮🇳 Tamil (தமிழ்)" sx={{ fontWeight: 700, textTransform: 'none' }} />
              <Tab value="en" label="🇺🇸 English (Original)" sx={{ fontWeight: 700, textTransform: 'none' }} />
            </Tabs>
          </Box>

          {loadingStudio ? (
            <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }} />
              <Typography variant="body2" color={isLight ? '#475569' : 'rgba(255,255,255,0.7)'}>
                Translating form content into Telugu, Hindi, and Tamil...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {studioFields.map((field) => {
                const currentText = studioTab === 'en' ? field.en : field[studioTab] || '';
                return (
                  <Paper
                    key={field.id}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                      borderColor: isLight ? '#E2E8F0' : 'rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={700} color={isLight ? '#0F172A' : '#F1F5F9'}>
                        {field.label}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={
                            copiedKey === `${field.id}_${studioTab}` ? (
                              <CheckIcon fontSize="small" />
                            ) : (
                              <ContentCopyIcon fontSize="small" />
                            )
                          }
                          onClick={() => handleCopyText(currentText, `${field.id}_${studioTab}`)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            color: isLight ? '#0D9488' : '#4CA1AF',
                          }}
                        >
                          {copiedKey === `${field.id}_${studioTab}` ? 'Copied' : 'Copy'}
                        </Button>
                      </Box>
                    </Box>

                    {studioTab !== 'en' && (
                      <Box
                        sx={{
                          mb: 1.5,
                          p: 1.2,
                          borderRadius: 1.5,
                          bgcolor: isLight ? '#EFF6FF' : 'rgba(59, 130, 246, 0.1)',
                          borderLeft: '3px solid #3B82F6',
                        }}
                      >
                        <Typography variant="caption" sx={{ color: isLight ? '#1E40AF' : '#93C5FD', fontWeight: 600 }}>
                          Original English:
                        </Typography>
                        <Typography variant="body2" sx={{ color: isLight ? '#1E293B' : '#E2E8F0', mt: 0.3 }}>
                          {field.en}
                        </Typography>
                      </Box>
                    )}

                    <TextField
                      fullWidth
                      multiline
                      rows={field.en.length > 80 ? 3 : 1}
                      value={currentText}
                      onChange={(e) => {
                        const val = e.target.value;
                        setStudioFields((prev) =>
                          prev.map((item) =>
                            item.id === field.id
                              ? { ...item, [studioTab === 'en' ? 'en' : studioTab]: val }
                              : item
                          )
                        );
                      }}
                      sx={{
                        bgcolor: isLight ? '#FFFFFF' : 'rgba(0, 0, 0, 0.25)',
                        '& .MuiOutlinedInput-root': {
                          fontFamily:
                            studioTab === 'te'
                              ? "'Noto Sans Telugu', sans-serif"
                              : studioTab === 'hi'
                              ? "'Noto Sans Devanagari', sans-serif"
                              : studioTab === 'ta'
                              ? "'Noto Sans Tamil', sans-serif"
                              : 'inherit',
                        },
                      }}
                    />
                  </Paper>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button onClick={() => setModalOpen(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => handleApplyStudioLanguage(studioTab)}
              sx={{
                bgcolor: isLight ? '#0D9488' : '#0D9488',
                color: '#FFFFFF',
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                borderRadius: '8px',
                '&:hover': { bgcolor: '#0B7A70' },
              }}
            >
              Apply {TARGET_LANGUAGES.find((l) => l.code === studioTab)?.label} to Form & Post
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          sx={{ fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UniversalFormTranslator;
